import { DATE_CHANGE_REASONS, mdy } from "@fieldpath/shared";
import type { DateAuthority, DateChangeReason, ReferenceOption, TaskStageView } from "@fieldpath/shared";
import { Fragment, useState } from "react";

import { ApiError } from "../api/client";
import { PAST_DATE_BOX } from "./DateHint";

/**
 * Planning the whole task at once: who, when and how long, every stage on one
 * grid.
 *
 * Setting this up stage by stage meant opening ten editors to answer the same
 * three questions ten times, and never seeing the shape of the plan while
 * making it — whether the dates run in order, whether one person has been
 * given everything, whether the hours add up to something anyone would agree
 * to.
 *
 * Rows are saved individually as they are edited rather than behind one big
 * Save. A partial plan is a normal state: people fill this in as they learn
 * the answers, and losing six rows because the seventh was invalid would be
 * unforgivable.
 */
export function StagePlan({
  stages,
  requiresHours,
  requiresDates,
  people,
  busy,
  onSave,
  onRequestDate,
  fromBooking = [],
  mayAssignOthers,
  stageWork,
  stageDates,
  meKey,
}: {
  stages: readonly TaskStageView[];
  /**
   * What a change to each stage's date would be, by stage id
   * (`editability.stageDates`, E5.2) -- the stage editor's answer. Only
   * `direct` gets a box; `request` is the date and "Request a change", as in
   * the editor (punch list F121, 30 Oct 2026). Until then every row had a box
   * the server refused for anyone but the task's PM or an admin.
   */
  stageDates: Readonly<Record<string, DateAuthority>>;
  /** Ask the task's PM to move one stage's date (E5.2). */
  onRequestDate: (
    stageId: string,
    body: { date: string | null; reasonCategory?: DateChangeReason; note?: string | null },
  ) => Promise<void>;
  /**
   * Stages whose people, date and hours come from the task's resource-scheduler booking
   * (`editability.schedulerOwnedStages`, 21 Oct 2026): shown, not offered.
   */
  fromBooking?: readonly string[];
  /**
   * May put somebody else on each stage or take them off, by stage id
   * (`editability.stageAssignOthers`: the accountable people, 22 Oct 2026, or a GIS
   * worker somebody else put on that stage, 29 Oct 2026). Elsewhere one may add or remove only oneself, as `meKey`.
   */
  mayAssignOthers: Readonly<Record<string, boolean>>;
  /**
   * May change each stage's hours, by stage id (`editability.stageWork`, punch
   * list F143, 30 Oct 2026): somebody on it, the task's PM / Task Manager or an
   * admin. Elsewhere the hours are shown, not offered.
   */
  stageWork: Readonly<Record<string, boolean>>;
  meKey: string | null;
  /** The workflow requires hours on staffed stages (E7.3). */
  requiresHours: boolean;
  /** The workflow requires due dates on staffed stages (E11). */
  requiresDates: boolean;
  people: ReferenceOption[];
  busy: boolean;
  onSave: (
    stageId: string,
    body: {
      dueDate?: string | null;
      allocatedHours?: number | null;
      assigneeKeys?: string[];
    },
  ) => Promise<void>;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  /** The stage whose date is being asked about, if any. */
  const [asking, setAsking] = useState<string | null>(null);

  /** False when refused, so a box can go back to what was saved. */
  const save = async (
    stageId: string,
    body: Parameters<typeof onSave>[1],
  ): Promise<boolean> => {
    setError(null);
    setSaving(stageId);
    try {
      await onSave(stageId, body);
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      return false;
    } finally {
      setSaving(null);
    }
  };

  const request = async (
    stageId: string,
    body: Parameters<typeof onRequestDate>[1],
  ): Promise<void> => {
    setError(null);
    setSaving(stageId);
    try {
      await onRequestDate(stageId, body);
      setAsking(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  const totalHours = stages.reduce((sum, s) => sum + (s.allocatedHours ?? 0), 0);
  const estimated = stages.filter((s) => s.allocatedHours !== null).length;

  return (
    <div className="card">
      <div className="section-head" style={{ marginTop: 0 }}>
        <h2>Plan</h2>
        <span className="count">
          {totalHours > 0
            ? `${trim(totalHours)} h across ${estimated} of ${stages.length} stages`
            : "no hours estimated yet"}
        </span>
      </div>

      {error ? <div className="error">{error}</div> : null}

      <table className="plan">
        <thead>
          <tr>
            <th>Stage</th>
            <th>Who</th>
            <th style={{ width: 150 }}>Due</th>
            <th style={{ width: 110 }}>Hours</th>
          </tr>
        </thead>
        <tbody>
          {stages.map((s) => fromBooking.includes(s.id) ? (
            <tr key={s.id} className={s.isCurrent ? "here" : ""}>
              <td>
                <span className={`state ${s.state}`} style={{ marginRight: 8 }}>
                  {s.state === "complete" ? "✓" : s.isCurrent ? "▸" : "·"}
                </span>
                {s.name}
                <div className="muted" style={{ fontSize: 12 }}>from the booking — change it in the scheduler</div>
              </td>
              <td>{s.assignees.length ? s.assignees.map((a) => a.label).join(", ") : "nobody"}</td>
              <td>{s.dueDate ? mdy(s.dueDate) : "—"}</td>
              <td>{s.allocatedHours === null ? "—" : `${trim(s.allocatedHours)} each`}</td>
            </tr>
          ) : (
            <Fragment key={s.id}>
              <tr className={s.isCurrent ? "here" : ""}>
                <td>
                  <span className={`state ${s.state}`} style={{ marginRight: 8 }}>
                    {s.state === "complete" ? "✓" : s.isCurrent ? "▸" : "·"}
                  </span>
                  {s.name}
                </td>

                <td>
                  <People
                    stage={s}
                    people={people}
                    onlyMe={mayAssignOthers[s.id] ? undefined : meKey}
                    disabled={busy || saving === s.id}
                    onChange={(keys) => void save(s.id, { assigneeKeys: keys })}
                  />
                </td>

                <td>
                  {stageDates[s.id] === "direct" ? (
                    <input
                      type="date"
                      {...PAST_DATE_BOX}
                      defaultValue={s.dueDate ?? ""}
                      disabled={busy || saving === s.id}
                      // Saved on blur, not on every keystroke: a date field fires
                      // change for each part typed, and a half-typed year is a
                      // date nobody meant. A refused save puts the saved date
                      // back, so the plan never shows one that is not (F121).
                      // A past date asks first (F170), and "Use date" leaves the
                      // box, which is what saves it.
                      onBlur={(e) => {
                        const box = e.currentTarget;
                        const next = box.value || null;
                        if (next !== (s.dueDate ?? null)) {
                          void save(s.id, { dueDate: next }).then((ok) => {
                            if (!ok) box.value = s.dueDate ?? "";
                          });
                        }
                      }}
                    />
                  ) : (
                    <span className="plan-date">
                      {s.dueDate ? mdy(s.dueDate) : "—"}
                      {stageDates[s.id] === "request" && asking !== s.id ? (
                        <button
                          type="button"
                          className="linkish"
                          disabled={busy || saving === s.id}
                          onClick={() => setAsking(s.id)}
                        >
                          Request a change
                        </button>
                      ) : null}
                    </span>
                  )}
                </td>

                <td>
                  {stageWork[s.id] ? (
                    <input
                      type="number"
                      min={0}
                      step={0.25}
                      placeholder="—"
                      defaultValue={s.allocatedHours ?? ""}
                      disabled={busy || saving === s.id}
                      onBlur={(e) => {
                        const box = e.currentTarget;
                        const raw = box.value.trim();
                        const next = raw === "" ? null : Number(raw);
                        if (next !== null && !Number.isFinite(next)) return;
                        if (next !== (s.allocatedHours ?? null)) {
                          void save(s.id, { allocatedHours: next }).then((ok) => {
                            if (!ok) box.value = s.allocatedHours === null ? "" : String(s.allocatedHours);
                          });
                        }
                      }}
                    />
                  ) : (
                    <span className="plan-date">
                      {s.allocatedHours === null ? "—" : `${s.allocatedHours} h`}
                    </span>
                  )}
                </td>
              </tr>
              {asking === s.id ? (
                <tr className="plan-ask">
                  <td colSpan={4}>
                    <AskDate
                      stage={s}
                      disabled={busy || saving === s.id}
                      onSend={(body) => void request(s.id, body)}
                      onCancel={() => setAsking(null)}
                    />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>

      <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
        Changes save as you leave each box. Moving a date later still asks why —
        open the stage itself for that.{" "}
        {/* What 0 means (E7.4), said once under the column rather than on
            every row. */}
        {requiresHours
          ? "Every stage with people on it needs hours here; a stage that only waits takes 0."
          : "A blank hours box means nobody has estimated it; 0 means deliberately no time."}
        {/* E11, said once under the table like the hours rule. Boxes save one
            at a time, so the order matters: date first, then people. */}
        {requiresDates
          ? " It also needs a due date — set the date before adding people. A stage waiting on the client or an agency, and the stages after it, can stay blank for now."
          : null}
        {/* Where a date box is missing, why (F121, 30 Oct 2026). */}
        {stages.every((s) => stageDates[s.id] === "direct" || fromBooking.includes(s.id))
          ? null
          : " Only the task's PM / Task Manager, its project's PM or TM or an admin sets dates; on a stage you are on, Request a change asks them."}
        {/* Where the people picker is missing, why (22 Oct 2026). */}
        {stages.every((s) => mayAssignOthers[s.id] || fromBooking.includes(s.id))
          ? null
          : " Only the task's PM / Task Manager, its deliverable's PM, the project's PM or TM or an admin puts other people on stages (GIS staff too, on stages someone else put them on); you can add or remove yourself."}
        {/* Where an hours box is missing, why (F143, 30 Oct 2026). */}
        {stages.every((s) => stageWork[s.id] || fromBooking.includes(s.id))
          ? null
          : " Only someone on a stage, the task's PM / Task Manager, its project's PM or TM or an admin changes its hours."}
      </p>
    </div>
  );
}

/**
 * Names with a remove, and one dropdown to add. For somebody who may not
 * assign others (`onlyMe` is their key, null when they are not linked to
 * Deltek and may change nothing), a remove on their own name only and
 * an Add me in place of the dropdown -- what the server will take from them.
 */
function People({
  stage,
  people,
  disabled,
  onChange,
  onlyMe,
}: {
  stage: TaskStageView;
  people: ReferenceOption[];
  disabled: boolean;
  onChange: (keys: string[]) => void;
  onlyMe?: string | null;
}) {
  const current = stage.assignees.map((a) => a.key);
  const free = people.filter((p) => !current.includes(p.key));
  const limited = onlyMe !== undefined;

  return (
    <div className="plan-people">
      {stage.assignees.map((a) => (
        <span className={limited && a.key !== onlyMe ? "person fixed" : "person"} key={a.key}>
          {a.label}
          {limited && a.key !== onlyMe ? null : (
            <button
              type="button"
              title={`Remove ${a.label}`}
              disabled={disabled}
              onClick={() => onChange(current.filter((k) => k !== a.key))}
            >
              ×
            </button>
          )}
        </span>
      ))}

      {limited ? (
        onlyMe === null || onlyMe === undefined || current.some((k) => k === onlyMe) ? null : (
          <button type="button" disabled={disabled} onClick={() => onChange([...current, onlyMe])}>
            + add me
          </button>
        )
      ) : (
        <select
          value=""
          disabled={disabled || free.length === 0}
          onChange={(e) => {
            if (e.target.value) onChange([...current, e.target.value]);
          }}
        >
          <option value="">{stage.assignees.length ? "+ add" : "nobody — add"}</option>
          {free.map((p) => (
            <option key={p.key} value={p.key}>
              {p.label}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Asking the task's PM to move one stage's date, under its row: the stage
 * editor's request (E5.2) -- the new date, why when it moves later, a note.
 */
function AskDate({
  stage,
  disabled,
  onSend,
  onCancel,
}: {
  stage: TaskStageView;
  disabled: boolean;
  onSend: (body: { date: string | null; reasonCategory?: DateChangeReason; note?: string | null }) => void;
  onCancel: () => void;
}) {
  const [date, setDate] = useState(stage.dueDate ?? "");
  const [reason, setReason] = useState<DateChangeReason | "">("");
  const [note, setNote] = useState("");
  // A reason only when it moves later, as everywhere a date changes.
  const later = stage.dueDate !== null && date !== "" && date > stage.dueDate;

  return (
    <div className="row plan-ask-row">
      <label htmlFor={`plan-ask-${stage.id}`}>Ask to move {stage.name} to</label>
      <input
        id={`plan-ask-${stage.id}`}
        type="date"
        {...PAST_DATE_BOX}
        value={date}
        onChange={(e) => setDate(e.target.value)}
      />
      {later ? (
        <select
          aria-label="Why it moves later"
          value={reason}
          onChange={(e) => setReason(e.target.value as DateChangeReason)}
        >
          {/* The stage editor's prompt, and not a choice: Send waits for a
              real reason (punch list F121, Jeremy's dev test 3.05, 30 Oct). */}
          <option value="" disabled hidden>
            Choose a reason…
          </option>
          {DATE_CHANGE_REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      ) : null}
      <input
        aria-label="Note for the PM"
        placeholder="Note for the PM (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button type="button" onClick={onCancel} disabled={disabled}>
        Never mind
      </button>
      <button
        type="button"
        className="primary"
        disabled={disabled || date === (stage.dueDate ?? "") || (later && !reason)}
        onClick={() =>
          onSend({
            date: date === "" ? null : date,
            ...(later && reason ? { reasonCategory: reason } : {}),
            note: note.trim() || null,
          })
        }
      >
        Send request
      </button>
    </div>
  );
}

/** 7.5 rather than 7.50, and 8 rather than 8.00. */
function trim(n: number): string {
  return String(Math.round(n * 100) / 100);
}
